"use client";

import { useQuery } from "@tanstack/react-query";
import {
  type ReactNode,
  useCallback,
  useMemo,
  useSyncExternalStore,
} from "react";
import { useSupabaseClient, useUser } from "../components/SupabaseProvider";
import type { PublicationModel } from "../types/models";
import {
  buildDemoPublications,
  demoAccountsFor,
  type DemoShort,
} from "../utils/demoMode";
import { DemoModeContext } from "./demoModeContext";
import { useProfile } from "./useProfile";
import { useSocialProviderAccounts } from "./useSocialProviderAccounts";

/** `/account?demo_mode=1` shows the toggle: the link we send creators. */
export const DEMO_MODE_QUERY_PARAM = "demo_mode";

const UNLOCKED_KEY = "klap-demo-mode-unlocked";
const enabledKey = (userId: string) => `klap-demo-mode:${userId}`;

/** The newest shorts only: three a day already fills 100 days. */
const MAX_DEMO_SHORTS = 300;

// Both flags live in this browser's localStorage, shared by every hook on
// the page and by other tabs. Storage can be unavailable (private windows,
// blocked site data): demo mode then simply stays off.
const listeners = new Set<() => void>();

const readFlag = (key: string) => {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const writeFlag = (key: string, on: boolean) => {
  try {
    if (on) localStorage.setItem(key, "1");
    else localStorage.removeItem(key);
  } catch {}
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
};

const useFlag = (key: string | null) =>
  useSyncExternalStore(
    subscribe,
    () => key !== null && readFlag(key),
    () => false,
  );

/**
 * The account page's toggle. `unlocked` once this browser has opened the demo
 * link; `enabled` per signed-in user, so another account on the same browser
 * starts with it off.
 */
export function useDemoModeSetting() {
  const userId = useUser()?.id;
  const key = userId ? enabledKey(userId) : null;
  return {
    enabled: useFlag(key),
    unlocked: useFlag(UNLOCKED_KEY),
    unlock: useCallback(() => writeFlag(UNLOCKED_KEY, true), []),
    setEnabled: useCallback(
      (on: boolean) => {
        if (key) writeFlag(key, on);
      },
      [key],
    ),
  };
}

/**
 * Wraps a page whose calendar and analytics should show demo data while demo
 * mode is on. Only what reads through `useDemoMode` changes: dialogs open at
 * app level, outside the scope, and keep working on the real schedule.
 */
export function DemoModeScope({ children }: { children: ReactNode }) {
  const { enabled } = useDemoModeSetting();
  if (!enabled) return <>{children}</>;
  return <DemoModeDataProvider>{children}</DemoModeDataProvider>;
}

function DemoModeDataProvider({ children }: { children: ReactNode }) {
  const supabase = useSupabaseClient();
  const user = useUser();
  const userId = user?.id;
  const { data: profile } = useProfile();
  // The provider is outside its own context: these are the real accounts.
  const { readAccounts } = useSocialProviderAccounts();
  const realAccounts = readAccounts.data ?? (readAccounts.isError ? [] : undefined);
  const name = profile?.name || user?.email?.split("@")[0] || "";
  const avatarUrl = profile?.avatarUrl ?? "";
  const accounts = useMemo(
    () => realAccounts && demoAccountsFor(realAccounts, { name, avatarUrl }),
    [realAccounts, name, avatarUrl],
  );

  const shorts = useQuery({
    queryKey: ["demo-mode", "shorts", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async (): Promise<DemoShort[]> => {
      const { data, error } = await supabase
        .from("videos_v2")
        .select("id, name, thumbnail_url, created_at, publication_captions")
        .eq("author_id", userId!)
        .eq("status", "ready")
        .not("folder_id", "is", null)
        .order("created_at", { ascending: false })
        .limit(MAX_DEMO_SHORTS);
      if (error) throw error;
      return data.map((row) => ({
        id: row.id,
        name: row.name,
        thumbnailUrl: row.thumbnail_url,
        createdAt: row.created_at,
        publicationCaptions:
          row.publication_captions as DemoShort["publicationCaptions"],
      }));
    },
  });

  const publications = useQuery<PublicationModel[], Error>({
    queryKey: [
      "demo-mode",
      "publications",
      userId,
      shorts.dataUpdatedAt,
      accounts?.map((account) => account.id).join(","),
    ],
    enabled: Boolean(userId && shorts.data && accounts),
    queryFn: () =>
      buildDemoPublications({
        shorts: shorts.data!,
        accounts: accounts!,
        ownerId: userId!,
        now: new Date(),
      }),
    // Booked posts turn posted as their time comes.
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  });

  const value = useMemo(
    () => ({ accounts, shorts: shorts.data, publications }),
    [accounts, shorts.data, publications],
  );
  // Nothing until the demo posts exist, or the pages would flash their
  // empty states (analytics' sample numbers) first.
  const ready =
    publications.data !== undefined || shorts.isError || publications.isError;
  return (
    <DemoModeContext.Provider value={value}>
      {ready ? children : null}
    </DemoModeContext.Provider>
  );
}
